import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { FileText, AlertTriangle, Scale, Globe, FileCheck, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';

export const TermsPage: React.FC = () => {
  const { t } = useTranslation();

  const sections = [
    {
      id: 'acceptance',
      icon: FileCheck,
      title: t('terms.sections.acceptance.title'),
      content: t('terms.sections.acceptance.content')
    },
    {
      id: 'use-license',
      icon: Scale,
      title: t('terms.sections.useLicense.title'),
      content: t('terms.sections.useLicense.content')
    },
    {
      id: 'disclaimer',
      icon: AlertTriangle,
      title: t('terms.sections.disclaimer.title'),
      content: t('terms.sections.disclaimer.content')
    },
    {
      id: 'limitations',
      icon: AlertTriangle,
      title: t('terms.sections.limitations.title'),
      content: t('terms.sections.limitations.content')
    },
    {
      id: 'revisions',
      icon: FileText,
      title: t('terms.sections.revisions.title'),
      content: t('terms.sections.revisions.content')
    },
    {
      id: 'governing-law',
      icon: Globe,
      title: t('terms.sections.governingLaw.title'),
      content: t('terms.sections.governingLaw.content')
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Helmet>
        <title>{t('terms.title')} - {t('header.title')}</title>
        <meta name="description" content={t('terms.description')} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${window.location.origin}/terms`} />
      </Helmet>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg">
              <FileText className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('terms.title')}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {t('terms.subtitle')}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">
            {t('terms.lastUpdated')}: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Introduction */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {t('terms.introduction')}
            </p>
          </CardContent>
        </Card>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <Card key={section.id} className="hover:shadow-lg transition-shadow duration-200">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <span className="text-xl font-semibold text-gray-900 dark:text-white">
                      {index + 1}. {section.title}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-gray dark:prose-invert max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: section.content }} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Footer */}
        <Card className="mt-12">
          <CardContent className="p-8 text-center">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {t('terms.questions.title')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('terms.questions.description')}
            </p>
            <div className="flex justify-center space-x-4">
              <a
                href="mailto:1481983952@qq.com"
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
              >
                <Mail className="w-4 h-4 mr-2" />
                {t('terms.questions.contact')}
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
